import { ChangeEvent, FC, useRef, useState, useMemo, useEffect } from 'react';
import DownArrow from '../../assets/down-arrow.png';
import SearchIcon from '../../assets/search.png';
import {
  OrganizationUnitChildItem,
  OrganizationUnitDropDownProps,
} from './organizationUnitDropdown.type';
import { useCloseOnOutsideClickAndEsc } from '../../utils/helper';
import {
  addUserCountToNestedItems,
  groupedChildOrganizationUnits,
} from '../../pages/userManagement/userManagementHelper';
import { renderOrganizationChildItems } from './organizationUnitDropdownHelper';
import { useOrganizationContext } from '../../service/context/organizationContext';

const OrganizationUnitDropDown: FC<OrganizationUnitDropDownProps> = (props) => {
  const dropdownRef = useRef<HTMLDivElement>(null);
  const [isOpenDropDownModal, setIsOpenDropDownModal] = useState(false);
  const [childSelectedLabel, setChildSelectedLabel] = useState('');
  const [searchTerm, setSearchTerm] = useState('');

  const { groupedOrganizationData } = useOrganizationContext();

  //  child organization summary
  const childOrganizationSummary: OrganizationUnitChildItem[] = useMemo(() => {
    return groupedChildOrganizationUnits(groupedOrganizationData);
  }, [groupedOrganizationData]);

  const firstChildOrganizationSummary: OrganizationUnitChildItem =
    childOrganizationSummary?.[0];

  // organization unit count using useMemo
  const nestedChildItemCounts: OrganizationUnitChildItem[] = useMemo(() => {
    if (childOrganizationSummary && props.users) {
      return addUserCountToNestedItems(childOrganizationSummary, props.users);
    }
    return [];
  }, [childOrganizationSummary, props.users]);

  useCloseOnOutsideClickAndEsc(dropdownRef, () => {
    setIsOpenDropDownModal(false);
  });

  useEffect(() => {
    if (!props.selectedLabel && firstChildOrganizationSummary) {
      setChildSelectedLabel(firstChildOrganizationSummary.label);
      props.onSelect?.(firstChildOrganizationSummary);
    }
  }, [props.selectedLabel, firstChildOrganizationSummary, props.onSelect]);

  const handleSelectItem = (item: OrganizationUnitChildItem) => {
    setChildSelectedLabel(item.label);
    props.onSelect?.(item);
    setIsOpenDropDownModal(false);
    setSearchTerm('');
  };

  const filterNestedItems = (
    items: OrganizationUnitChildItem[],
    term: string,
  ): OrganizationUnitChildItem[] => {
    return items
      .map((item: OrganizationUnitChildItem) => {
        const childOrganizationLabel: boolean = item.label
          .toLowerCase()
          .includes(term.toLowerCase());

        const filteredChildItem: OrganizationUnitChildItem[] =
          item.nestedChildItem
            ? filterNestedItems(item.nestedChildItem, term)
            : [];

        if (childOrganizationLabel || filteredChildItem.length > 0) {
          return {
            ...item,
            nestedChildItem: filteredChildItem,
          };
        }

        return null;
      })
      .filter(Boolean) as OrganizationUnitChildItem[];
  };

  const filteredData: OrganizationUnitChildItem[] = useMemo(() => {
    if (!searchTerm) return nestedChildItemCounts;
    return filterNestedItems(nestedChildItemCounts, searchTerm);
  }, [nestedChildItemCounts, searchTerm]);

  return (
    <div
      ref={dropdownRef}
      className={`relative w-72 ${props.containerClassName}`}
    >
      <button
        onClick={() => {
          if (props.isDisabled) return;
          setIsOpenDropDownModal(!isOpenDropDownModal);
        }}
        className={`w-full px-4 py-2  ${
          props.isDisabled ? 'bg-[#F5F5F5] cursor-not-allowed' : ''
        } border border-[#E3E3E3] shadow-sm flex justify-between items-center ${
          isOpenDropDownModal
            ? 'border-b-0 rounded-t-[20px] rounded-b-none'
            : `rounded-[20px]`
        } ${props.buttonClassName || 'bg-white'}`}
      >
        <span className="truncate">{props?.selectedLabel?.label}</span>
        <img
          src={DownArrow}
          alt="Toggle Dropdown"
          className="w-[11px] h-[6px]"
        />
      </button>

      {isOpenDropDownModal && (
        <div className="absolute z-30 w-full bg-white border border-[#E3E3E3] border-t-0 rounded-b-[20px] rounded-t-none shadow-lg max-h-60 overflow-auto">
          <div className="p-2 border-t flex items-center gap-2">
            <img src={SearchIcon} alt="Search Icon" className="h-4 w-4 ml-2" />
            <input
              type="text"
              value={searchTerm}
              onChange={(e: ChangeEvent<HTMLInputElement>) =>
                setSearchTerm(e.target.value)
              }
              className="w-full px-2 py-1 text-base focus:outline-none"
              placeholder="Search"
            />
          </div>

          <ul className="px-2 pb-2">
            {filteredData.map(
              (organizationChildItem: OrganizationUnitChildItem) => (
                <li key={organizationChildItem.value}>
                  {renderOrganizationChildItems(
                    [organizationChildItem],
                    0,
                    childSelectedLabel,
                    handleSelectItem,
                  )}
                </li>
              ),
            )}
          </ul>
        </div>
      )}
    </div>
  );
};

export default OrganizationUnitDropDown;
